import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Code2, Calendar, FileText, ArrowRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const hStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 800,
  color: 'var(--bw-dark)',
  letterSpacing: '-0.025em',
  lineHeight: 1.15,
};

const SquareOneAlumni = () => {
  const included = [
    {
      Icon: CheckCircle,
      title: 'Complete MVP Development',
      description: 'Full-stack web application built to production standards. This is the same quality work we deliver to paying clients (valued at $10K).',
    },
    {
      Icon: FileText,
      title: 'Technical Consulting',
      description: 'We\'ll help you decide what to build, scope it clearly, and make technical decisions with confidence before any code is written.',
    },
    {
      Icon: Code2,
      title: 'You Own the Code',
      description: '100% code ownership with clear documentation. When we\'re done, you can maintain it yourself, hire a team, or continue working with us.',
    },
    {
      Icon: Calendar,
      title: 'Weekly Check-ins',
      description: 'Regular progress updates and feedback sessions. You\'ll see working software every week as we build. No black box development.',
    },
  ];

  const whoShouldApply = [
    {
      title: 'Square One (SQ1) Alumni',
      description: 'You\'ve completed the full Square One bootcamp and received your certificate of completion. You must have a referral and support from your SQ1 mentor to apply.',
    },
    {
      title: 'Clear Product Vision',
      description: 'You know what problem you\'re solving and who you\'re solving it for. You don\'t need to have all the technical answers — we\'ll help with that.',
    },
    {
      title: 'Ready to Build',
      description: 'You\'re committed to launching an MVP in the next 2–3 months. This isn\'t for research or exploration — it\'s for founders ready to ship.',
    },
    {
      title: 'Open to Collaboration',
      description: 'You\'re willing to work with us on scope, prioritization, and technical decisions. We\'ll guide you, but this is a partnership.',
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bw-white)' }}>
      <Helmet>
        <title>Square One (SQ1) Alumni Program — BrightWings</title>
        <meta name="description" content="One free MVP build per year for Square One alumni. You finished the bootcamp — BrightWings helps you build what comes next." />
        <link rel="canonical" href="https://brightwings.io/squareone-alumni" />
        <meta property="og:url" content="https://brightwings.io/squareone-alumni" />
        <meta property="og:title" content="Square One (SQ1) Alumni Program — BrightWings" />
        <meta property="og:description" content="One free MVP build per year for Square One alumni. You finished the bootcamp — BrightWings helps you build what comes next." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Square One (SQ1) Alumni Program — BrightWings" />
        <meta name="twitter:description" content="One free MVP build per year for Square One alumni. You finished the bootcamp — BrightWings helps you build what comes next." />
      </Helmet>
      <Navigation />

      {/* Hero */}
      <section className="bw-hero">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bw-eyebrow-pill">Square One (SQ1) Alumni</div>
            <h1 style={{ ...hStyle, fontSize: 'clamp(36px, 5.5vw, 68px)', marginBottom: '20px' }}>
              You finished the bootcamp. <em style={{ fontStyle: 'normal', color: 'var(--bw-blue)' }}>Now what?</em>
            </h1>
            <p style={{ fontSize: 'clamp(16px, 2vw, 20px)', color: 'var(--bw-text-mid)', lineHeight: 1.7, maxWidth: '560px', margin: '0 auto 24px' }}>
              You've put in the work. You have the idea. But building real software without a technical partner is a different kind of challenge — and most founders don't make it past this step.
            </p>
            <p style={{ fontSize: '16px', color: 'var(--bw-text-mid)', lineHeight: 1.7, maxWidth: '540px', margin: '0 auto 24px' }}>
              BrightWings works with Square One alumni because we believe the best outcomes happen when business clarity meets technical direction. Once a year, we select one founder to receive that partnership at no cost.
            </p>
            <p style={{ fontSize: '15px', color: 'var(--bw-text-mid)' }}>
              Not familiar with Square One?{' '}
              <a
                href="https://www.cortexstl.org/learn/entrepreneur-training/sq1-bootcamp"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--bw-blue)', fontWeight: 600, textDecoration: 'underline' }}
              >
                Learn more about the SQ1 bootcamp
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <div className="bw-stats-bar">
        <div className="max-w-4xl mx-auto w-full flex flex-wrap justify-center">
          {[
            { val: '$10K', lbl: 'Value' },
            { val: '1/yr', lbl: 'Selected per year' },
            { val: '100%', lbl: 'Code ownership' },
          ].map((s, i) => (
            <div key={i} className="bw-stat-item">
              <div className="bw-stat-val">{s.val}</div>
              <div className="bw-stat-lbl">{s.lbl}</div>
            </div>
          ))}
        </div>
      </div>

      {/* What's Included */}
      <section className="bw-section-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 42px)', marginBottom: '12px' }}>What's Included</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {included.map((item, i) => (
              <div key={i} className="bw-feature-card">
                <div className="bw-feature-icon">
                  <item.Icon size={20} color="var(--bw-blue)" />
                </div>
                <h3 style={{ ...hStyle, fontSize: '18px', marginBottom: '10px' }}>{item.title}</h3>
                <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.7, fontSize: '15px' }}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Should Apply */}
      <section className="bw-section-surface">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 42px)', marginBottom: '12px' }}>Who Should Apply</h2>
          </div>
          <div className="max-w-4xl space-y-6">
            {whoShouldApply.map((item, i) => (
              <div key={i} className="flex items-start gap-4">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
                  style={{ backgroundColor: 'var(--bw-blue)' }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="white" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 style={{ ...hStyle, fontSize: '18px', marginBottom: '6px' }}>{item.title}</h3>
                  <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.7, fontSize: '15px' }}>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Selection Criteria */}
      <section className="bw-section-white">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-4xl">
            <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 42px)', marginBottom: '16px' }}>How We Select</h2>
            <p style={{ fontSize: '18px', color: 'var(--bw-text-mid)', marginBottom: '40px', lineHeight: 1.7 }}>
              We review applications on a rolling basis and select one founder per year. Here's what we look for:
            </p>
            <div className="space-y-4">
              {[
                {
                  title: 'Problem Clarity',
                  body: 'You can clearly articulate the problem you\'re solving and why it matters. You don\'t need to know how to solve it technically — that\'s our job.',
                },
                {
                  title: 'Founder Commitment',
                  body: 'You\'re all-in on this. You have time to collaborate with us, respond to questions, and test what we build as we build it.',
                },
                {
                  title: 'Mutual Fit',
                  body: 'We work best with founders who value clear communication, honest feedback, and building the right thing — not just building fast. If that sounds like you, we want to hear from you.',
                },
              ].map((item, i) => (
                <div key={i} className="bw-card">
                  <div className="bw-card-body">
                    <h3 style={{ ...hStyle, fontSize: '18px', marginBottom: '8px' }}>{item.title}</h3>
                    <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.7, fontSize: '15px' }}>{item.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stakes */}
      <section className="bw-section-surface">
        <div className="max-w-4xl mx-auto">
          <h2 style={{ ...hStyle, fontSize: 'clamp(22px, 3vw, 32px)', marginBottom: '16px' }}>Don't stall after the bootcamp.</h2>
          <p style={{ fontSize: '18px', color: 'var(--bw-text-mid)', lineHeight: 1.7, maxWidth: '640px' }}>
            The founders who stall after SQ1 aren't less capable. They're building without the right partner. This is a chance to not be one of them.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bw-cta-section">
        <div className="max-w-4xl mx-auto">
          <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 44px)', marginBottom: '16px' }}>Ready to Apply?</h2>
          <p style={{ fontSize: '18px', color: 'var(--bw-text-mid)', marginBottom: '36px', lineHeight: 1.7 }}>
            Tell us about your project. We'll review your application and reach out within one week if we think there's a fit.
          </p>
          <Link to="/squareone-alumni/apply" onClick={() => window.scrollTo(0, 0)} className="bw-btn-primary">
            Submit Application
            <ArrowRight size={16} />
          </Link>
          <p className="mt-6 text-sm" style={{ color: 'var(--bw-muted)' }}>
            Applications reviewed on a rolling basis. One free build awarded per year.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SquareOneAlumni;
